import type { TugState, TugTeam } from "@/lib/types";

export const TEAM_KEYS: TugTeam[] = ["A", "B"];

// 서버 값이 뭐든 A / B 로 정규화
export function teamKey(v?: string): TugTeam {
  return v === "B" ? "B" : "A";
}

// 팀 이름 (서버가 안 주면 기본값)
export function teamName(v: string | undefined, t: TugTeam): string {
  const s = (v ?? "").trim();
  if (s) return s;
  return t === "A" ? "A팀" : "B팀";
}

// 팀별 색. StyleSystem 토큰(primary / accent)만 사용한다.
export const TEAM_BG: Record<TugTeam, string> = {
  A: "bg-primary-500 text-background-50",
  B: "bg-accent-500 text-foreground-950",
};

export const TEAM_SOFT: Record<TugTeam, string> = {
  A: "border-primary-300 bg-primary-50",
  B: "border-accent-300 bg-accent-50",
};

export const TEAM_STRIP: Record<TugTeam, string> = {
  A: "bg-primary-500",
  B: "bg-accent-500",
};

export const TEAM_TEXT: Record<TugTeam, string> = {
  A: "text-primary-700",
  B: "text-accent-700",
};

export const TEAM_RING: Record<TugTeam, string> = {
  A: "oklch(var(--primary-500))",
  B: "oklch(var(--accent-500))",
};

export const STATE_BADGE: Record<TugState, string> = {
  open: "bg-secondary-100 text-secondary-900",
  closed: "bg-background-200 text-foreground-500",
};

export const STATE_BORDER: Record<TugState, string> = {
  open: "border-secondary-400",
  closed: "border-background-200",
};

export const STATE_STRIP: Record<TugState, string> = {
  open: "bg-secondary-500",
  closed: "bg-background-300",
};

export function stateLabel(state?: string, stateTxt?: string): string {
  if (stateTxt) return stateTxt;
  return state === "closed" ? "종료" : "진행중";
}

export function isDim(state?: string): boolean {
  return state === "closed";
}

// 팀 인원차 허용 기본값
export const DEFAULT_BALANCE = 1;

// 승부 기준 문구 (top = 동수 상위합계가 기본)
export function judgeLabel(mode?: string): string {
  if (mode === "avg") return "1인 평균";
  if (mode === "top") return "동수 상위합계";
  return "전원 합계";
}

// 서버가 mode_label 을 주면 그걸 우선 사용
export function modeLabel(mode?: string, label?: string): string {
  const s = (label ?? "").trim();
  return s || judgeLabel(mode);
}

// 인원차 허용 라벨
export function balanceLabel(v?: number): string {
  if (v === 0) return "제한없음";
  if (v === 2) return "2명";
  return "1명";
}

export interface TugCountdown {
  text: string;
  urgent: boolean;
  expired: boolean;
}

// 마감까지 남은 초. 1시간 이상 "1:12:30", 미만 "38:12". 5분 미만은 urgent.
export function formatCountdown(totalSeconds: number): TugCountdown {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const urgent = s > 0 && s < 300;
  if (h > 0) return { text: `${h}:${pad(m)}:${pad(sec)}`, urgent, expired: s <= 0 };
  return { text: `${pad(m)}:${pad(sec)}`, urgent, expired: s <= 0 };
}

// "2026-09-14 13:20:00" → "09.14 13:20"
export function formatDeadline(value?: string): string {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatAvg(v?: number): string {
  if (typeof v !== "number" || Number.isNaN(v)) return "-";
  return v.toFixed(1);
}